import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useMemo } from "react";
import { BillLineItem } from "@common/types/bill";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useViewTableMeta } from "@hooks/view/useViewTableMeta";
import { SchedulePicker } from "@components/accounting/schedules/SchedulePicker";
import { resolvePickerTemplates } from "@components/accounting/schedules/schedulePickerTemplates";
import { toScheduleCardValues } from "@components/invoices/views/scheduleCellState";
import { BillScheduleCellContent } from "@components/bills/schedules/BillScheduleCellContent";
import {
    isBillScheduleCellInteractive,
    resolveBillLineScheduleCellState,
} from "@components/bills/schedules/billScheduleCellState";
import { BillScheduleTableOptions } from "@components/bills/schedules/useBillScheduleTableOptions";
import { useBillSchedulePicker } from "@components/bills/schedules/useBillSchedulePicker";
import { BillLineItemsViewCallbacks } from "../types";

export const ScheduleCell = memo(
    ({ row, table }: CellContext<ViewTableFeatures, BillLineItem, unknown>) => {
        const { callbacks, options } = useViewTableMeta<
            BillLineItemsViewCallbacks,
            BillScheduleTableOptions
        >(table);
        const lineItem = row.original;
        const bill = lineItem.bill;
        const state = useMemo(
            () => resolveBillLineScheduleCellState(lineItem),
            [lineItem],
        );
        const isInteractive = isBillScheduleCellInteractive(state);

        const scheduleTemplates = useMemo(
            () =>
                resolvePickerTemplates({
                    templates: options?.scheduleTemplates,
                    entityId: bill?.entityId,
                    prioritizedTemplateId:
                        options?.prioritizedScheduleTemplateId,
                }),
            [
                options?.scheduleTemplates,
                options?.prioritizedScheduleTemplateId,
                bill?.entityId,
            ],
        );

        const scope = useMemo(
            () => ({
                amount: lineItem.amount,
                sourceLineIds: [lineItem.id],
                sourceLabel:
                    lineItem.description ?? bill?.invoiceNumber ?? "Bill line",
            }),
            [bill?.invoiceNumber, lineItem],
        );

        const { pickerProps, overlays } = useBillSchedulePicker({
            bill,
            state: isInteractive ? state : null,
            scope,
            callbacks,
        });

        if (!isInteractive) {
            return (
                <ViewTableCell>
                    <BillScheduleCellContent state={state} />
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks>
                <SchedulePicker
                    templates={scheduleTemplates}
                    applied={toScheduleCardValues(state)}
                    prioritizedTemplateId={
                        options?.prioritizedScheduleTemplateId
                    }
                    {...pickerProps}
                />
                {overlays}
            </ViewTableCell>
        );
    },
);

ScheduleCell.displayName = "ScheduleCell";
